import _ from 'underscore';
import PropTypes from 'prop-types';
import { isCurrentThirtyDays } from 'javascripts/analytics/config/utilities';
import React from 'react';
import { capitalizeCountryName } from 'javascripts/analytics/utils/country_names';

import { ImpactBox } from './impact_box.wjs.jsx';
import { ImpactTableBodyCell } from './impact_table_body_cell.wjs.jsx';
import { ImpactTopStat } from './impact_top_stat.wjs.jsx';
import { trackAnalyticsClick } from 'js/analytics/metrics/analyticsTracking';

const MIN_PERCENTILE_FOR_TABLE_ROW = 0.001;
const MIN_PERCENTILE_FOR_HEADLINE = 0.101; // We don't ever want to show "90%", since that sounds good but is bad

export class ImpactBoxes extends React.Component {
  static propTypes = {
    displayedDates: PropTypes.object.isRequired,
    userPercentile: PropTypes.object.isRequired,

    visitorCount: PropTypes.number.isRequired,
    visitorCountFetching: PropTypes.bool.isRequired,

    downloadCount: PropTypes.number.isRequired,
    downloadCountFetching: PropTypes.bool.isRequired,

    hitCount: PropTypes.number.isRequired,
    hitCountFetching: PropTypes.bool.isRequired,

    topCountries: PropTypes.array.isRequired,
    topCountriesFetching: PropTypes.bool.isRequired,

    topCities: PropTypes.array.isRequired,
    topCitiesFetching: PropTypes.bool.isRequired,

    topUniversities: PropTypes.array.isRequired,
    topUniversitiesFetching: PropTypes.bool.isRequired,

    topPositions: PropTypes.array.isRequired,
    topPositionsFetching: PropTypes.bool.isRequired,

    // This is for overall impact only
    topDocs: PropTypes.array,
    topDocsFetching: PropTypes.bool,

    // This is for single work impact only
    furthestPages: PropTypes.array,
    furthestPagesFetching: PropTypes.bool,

    topTrafficSources: PropTypes.array.isRequired,
    topTrafficSourcesFetching: PropTypes.bool.isRequired,

    topSearches: PropTypes.array.isRequired,
    topSearchesFetching: PropTypes.bool.isRequired,

    topResearchInterestPercentiles: PropTypes.array.isRequired,
    topResearchInterestPercentilesFetching: PropTypes.bool.isRequired,

    readerResearchInterestCount: PropTypes.number,
    topReaderResearchInterests: PropTypes.array.isRequired,
    topReaderResearchInterestsFetching: PropTypes.bool.isRequired,

    hideResearchField: PropTypes.bool,
    hidePercentile: PropTypes.bool,

    twelveMonth: PropTypes.bool,
  };

  maxPanelTableRows = 5;

  renderCityRows = (options) => {
    return _.chain(this.props.topCities)
      .sortBy('visitor_count')
      .reverse()
      .first(options.maxRows)
      .map(
        function (topCity) {
          return (
            <tr key={topCity.city.name}>
              <ImpactTableBodyCell className="u-textTruncate u-displayTableCell">
                <span title={topCity.city.name}>{topCity.city.name}</span>
              </ImpactTableBodyCell>
              <ImpactTableBodyCell className="u-taRight">
                {topCity.visitor_count}
              </ImpactTableBodyCell>
            </tr>
          );
        }.bind(this),
      )
      .value();
  };

  nameFromCountry = (country) => {
    return capitalizeCountryName(country.countryName);
  };

  renderCountryRows = (options) => {
    return _.chain(this.props.topCountries)
      .sortBy('visitor_count')
      .reverse()
      .first(options.maxRows)
      .map(
        function (topCountry) {
          const name = this.nameFromCountry(topCountry.country);
          return (
            <tr key={topCountry.country.code}>
              <ImpactTableBodyCell className="u-textTruncate u-displayTableCell">
                <span title={name}>{name}</span>
              </ImpactTableBodyCell>
              <ImpactTableBodyCell className="u-taRight">
                {topCountry.visitor_count}
              </ImpactTableBodyCell>
            </tr>
          );
        }.bind(this),
      )
      .value();
  };

  renderPositionRows = (options) => {
    return _.chain(this.props.topPositions)
      .sortBy('visitor_count')
      .reverse()
      .first(options.maxRows)
      .map(
        function (topPosition) {
          return (
            <tr key={topPosition.position.name} className="u-ph4x">
              <ImpactTableBodyCell className="u-textTruncate u-displayTableCell">
                <span title={topPosition.position.name}>
                  {topPosition.position.name}
                </span>
              </ImpactTableBodyCell>
              <ImpactTableBodyCell className="u-taRight">
                {topPosition.visitor_count}
              </ImpactTableBodyCell>
            </tr>
          );
        }.bind(this),
      )
      .value();
  };

  renderSearchRows = (options) => {
    return _.chain(this.props.topSearches)
      .sortBy('hit_count')
      .reverse()
      .first(options.maxRows)
      .map(
        function (topSearch) {
          return (
            <tr key={topSearch.search.source.name} className="u-ph4x">
              <ImpactTableBodyCell className="u-textTruncate u-displayTableCell">
                <span title={topSearch.search.source.name}>
                  {topSearch.search.source.name}
                </span>
              </ImpactTableBodyCell>
              <ImpactTableBodyCell className="u-taRight">
                {topSearch.hit_count}
              </ImpactTableBodyCell>
            </tr>
          );
        }.bind(this),
      )
      .value();
  };

  renderTrafficSourceRows = (options) => {
    let trafficSources = _.chain(this.props.topTrafficSources)
      .sortBy('hit_count')
      .reverse()
      .first(options.maxRows)
      .value();

    trafficSources =
      this.addOtherToTrafficSourceNamesWhereNeeded(trafficSources);

    return _.map(
      trafficSources,
      function (topSource) {
        return (
          <tr key={topSource.traffic_source.source.name} className="u-ph4x">
            <ImpactTableBodyCell className="u-textTruncate u-displayTableCell">
              <span title={topSource.traffic_source.source.name}>
                {topSource.traffic_source.source.name}
              </span>
            </ImpactTableBodyCell>
            <ImpactTableBodyCell className="u-taRight">
              {topSource.hit_count}
            </ImpactTableBodyCell>
          </tr>
        );
      }.bind(this),
    );
  };

  // Convert "News Feed" to "News Feed (other)" if there is also a row for
  // e.g. "News Feed (as bookmark)", and do something similar for "Email" and
  // "Academia.edu"
  addOtherToTrafficSourceNamesWhereNeeded = (trafficSources) => {
    const names = _.map(trafficSources, function (ts) {
      return ts.traffic_source.source.name;
    });

    return _.map(trafficSources, function (row) {
      const name = row.traffic_source.source.name;

      const isGeneric = _.contains(
        ['Academia.edu', 'News Feed', 'Email'],
        name,
      );
      const specificVariantAlsoAppears = _.any(names, function (other) {
        return (
          // NATIONAL PARKS: Please remove this disable and try to fix it if you're here
          // eslint-disable-next-line eqeqeq
          other.length > name.length && other.substr(0, name.length) == name
        );
      });

      if (isGeneric && specificVariantAlsoAppears) {
        return _.extend({}, row, {
          traffic_source: _.extend({}, row.traffic_source, {
            name: name + ' (other)',
          }),
        });
      } else {
        return row;
      }
    });
  };

  renderMultiWorkPagesReadRows = (options) => {
    return _.chain(this.props.topDocs)
      .sortBy('pages_read')
      .reverse()
      .first(options.maxRows)
      .map(
        function (topDoc) {
          return (
            <tr key={topDoc.doc.id} className="u-ph4x">
              <ImpactTableBodyCell className="u-textTruncate u-displayTableCell">
                <a
                  href={'#/activity/documents/' + topDoc.doc.id}
                  className="u-tcGrayDarkest"
                  title={topDoc.doc.name}
                  onClick={() =>
                    trackAnalyticsClick(
                      'analytics-content-link',
                      { link_type: 'paper', table: 'impact' },
                      Number.isInteger(topDoc.doc.id)
                        ? topDoc.doc.id
                        : undefined,
                    )
                  }
                >
                  {topDoc.doc.name}
                </a>
              </ImpactTableBodyCell>
              <ImpactTableBodyCell className="u-taRight">
                {topDoc.pages_read}
              </ImpactTableBodyCell>
            </tr>
          );
        }.bind(this),
      )
      .value();
  };

  renderSingleWorkPagesReadRows = (options) => {
    const buckets = this.props.furthestPages.length
      ? this.bucketFurthestPages(this.props.furthestPages, options)
      : [];
    return _.map(buckets, function (bucket) {
      const description =
        // NATIONAL PARKS: Please remove this disable and try to fix it if you're here
        // eslint-disable-next-line eqeqeq
        bucket.lte == bucket.gte
          ? '' + bucket.lte + ' ' + $h.pluralize(bucket.lte, 'page')
          : '' + bucket.gte + '-' + bucket.lte + ' pages';

      return (
        <tr key={description} className="u-ph4x">
          <ImpactTableBodyCell className="u-textTruncate u-displayTableCell">
            {description}
          </ImpactTableBodyCell>
          <ImpactTableBodyCell className="u-taRight">
            {bucket.count}
          </ImpactTableBodyCell>
        </tr>
      );
    });
  };

  renderUniversityRows = (options) => {
    return _.chain(this.props.topUniversities)
      .sortBy('visitor_count')
      .reverse()
      .first(options.maxRows)
      .map(
        function (topUniversity) {
          return (
            <tr key={topUniversity.university.url} className="u-ph4x">
              <ImpactTableBodyCell className="u-textTruncate u-displayTableCell">
                <a
                  href={topUniversity.university.url}
                  className="u-tcGrayDarkest"
                  title={topUniversity.university.name}
                  onClick={() =>
                    trackAnalyticsClick(
                      'analytics-content-link',
                      { link_type: 'university', table: 'impact' },
                      Number.isInteger(topUniversity.university.id)
                        ? topUniversity.university.id
                        : undefined,
                    )
                  }
                >
                  {topUniversity.university.name}
                </a>
              </ImpactTableBodyCell>
              <ImpactTableBodyCell className="u-taRight">
                {topUniversity.visitor_count}
              </ImpactTableBodyCell>
            </tr>
          );
        }.bind(this),
      )
      .value();
  };

  renderResearchInterestRows = (options) => {
    return _.chain(this.props.topReaderResearchInterests)
      .sortBy('visitor_count')
      .reverse()
      .first(options.maxRows)
      .map(
        function (topRi) {
          return (
            <tr key={topRi.research_interest.id} className="u-ph4x">
              <ImpactTableBodyCell className="u-textTruncate u-displayTableCell">
                <a
                  href={topRi.research_interest.url}
                  className="u-tcGrayDarkest"
                  title={topRi.research_interest.name}
                  onClick={() =>
                    trackAnalyticsClick(
                      'analytics-content-link',
                      { link_type: 'research_interest', table: 'impact' },
                      Number.isInteger(topRi.research_interest.id)
                        ? topRi.research_interest.id
                        : undefined,
                    )
                  }
                >
                  {topRi.research_interest.name}
                </a>
              </ImpactTableBodyCell>
              <ImpactTableBodyCell className="u-taRight">
                {topRi.visitor_count}
              </ImpactTableBodyCell>
            </tr>
          );
        }.bind(this),
      )
      .value();
  };

  renderResearchInterestPercentileRows = (options) => {
    return _.chain(this.props.topResearchInterestPercentiles)
      .filter(function (p) {
        return p.percentile >= MIN_PERCENTILE_FOR_TABLE_ROW;
      })
      .sortBy('percentile')
      .reverse()
      .first(options.maxRows)
      .map(
        function (p) {
          return (
            <tr key={p.research_interest.id} className="u-ph4x">
              <ImpactTableBodyCell className="u-textTruncate u-displayTableCell">
                <a
                  href={p.research_interest.url}
                  className="u-tcGrayDarkest"
                  title={p.research_interest.name}
                  onClick={() =>
                    trackAnalyticsClick(
                      'analytics-content-link',
                      { link_type: 'research_interest', table: 'impact' },
                      Number.isInteger(p.research_interest.id)
                        ? p.research_interest.id
                        : undefined,
                    )
                  }
                >
                  {p.research_interest.name}
                </a>
              </ImpactTableBodyCell>
              <ImpactTableBodyCell className="u-taRight">
                {this.percentileText(p.percentile)}
              </ImpactTableBodyCell>
            </tr>
          );
        }.bind(this),
      )
      .value();
  };

  percentileText = (number) => {
    const raw = ((1.0 - number) * 100).toFixed(1);

    if (parseFloat(raw) <= 0) {
      return '0.1%';
    } else if (parseFloat(raw) >= 100) {
      return '99.9%';
    } else {
      return raw + '%';
    }
  };

  getAllBoxes = () => {
    const boxes = [];

    const dateRange = this.props.twelveMonth
      ? '12-Month '
      : this.props.displayedDates.dayCount + '-Day ';

    boxes.push({
      statCount: this.props.topCountries.length,
      statNoun: 'Country',
      columnLabels: ['Country', dateRange + 'Visitors'],
      panelRows: this.renderCountryRows({ maxRows: this.maxPanelTableRows }),
      modalRows: this.renderCountryRows({ maxRows: Infinity }),
      loading: this.props.topCountriesFetching,
    });

    boxes.push({
      statCount: this.props.topCities.length,
      statNoun: 'City',
      columnLabels: ['City', dateRange + 'Visitors'],
      panelRows: this.renderCityRows({ maxRows: this.maxPanelTableRows }),
      modalRows: this.renderCityRows({ maxRows: Infinity }),
      loading: this.props.topCitiesFetching,
    });

    boxes.push({
      statCount: this.props.topUniversities.length,
      statNoun: 'University',
      columnLabels: ['University', dateRange + 'Visitors'],
      panelRows: this.renderUniversityRows({ maxRows: this.maxPanelTableRows }),
      modalRows: this.renderUniversityRows({ maxRows: Infinity }),
      loading: this.props.topUniversitiesFetching,
    });

    if (!this.props.hideResearchField) {
      boxes.push({
        statCount:
          this.props.readerResearchInterestCount ||
          this.props.topReaderResearchInterests.length,
        statNoun: 'Research Field',
        columnLabels: ['Research Field', dateRange + 'Visitors'],
        panelRows: this.renderResearchInterestRows({
          maxRows: this.maxPanelTableRows,
        }),
        modalRows: this.renderResearchInterestRows({ maxRows: Infinity }),
        loading: this.props.topReaderResearchInterestsFetching,
      });
    }

    if (!this.props.hidePercentile) {
      const percentile =
        this.havePercentileData() &&
        this.props.userPercentile.model.percentile >=
          MIN_PERCENTILE_FOR_HEADLINE &&
        this.percentileText(this.props.userPercentile.model.percentile);

      boxes.push({
        statCount: percentile || '',
        statCountExtra: !percentile && this.percentileDisclaimer(),
        statLabel: percentile
          ? 'Top ' + percentile + ' By Views'
          : 'Percent By Views',
        columnLabels: ['Research Field', 'Top % By ' + dateRange + ' Views'],
        panelRows: percentile
          ? this.renderResearchInterestPercentileRows({
              maxRows: this.maxPanelTableRows,
            })
          : [],
        modalRows: percentile
          ? this.renderResearchInterestPercentileRows({ maxRows: Infinity })
          : [],
        loading: this.props.topResearchInterestPercentilesFetching,
      });
    }

    boxes.push({
      statCount: this.props.topPositions.length,
      statNoun: 'Job Title',
      columnLabels: ['Job Title', dateRange + 'Visitors'],
      panelRows: this.renderPositionRows({ maxRows: this.maxPanelTableRows }),
      modalRows: this.renderPositionRows({ maxRows: Infinity }),
      loading: this.props.topPositionsFetching,
    });

    if (this.props.singleWorkMode) {
      boxes.push({
        // Make this sum furthest pages instead of get length
        statCount: _.reduce(
          this.props.furthestPages,
          function (memo, furthest) {
            return memo + furthest;
          },
          0,
        ),
        statLabel: 'Pages Read',
        statCountExtra: this.pagesReadDisclaimer(),
        columnLabels: ['Pages Read Per View', dateRange + 'Count'],
        panelRows: this.renderSingleWorkPagesReadRows({
          maxRows: this.maxPanelTableRows,
        }),
        modalRows: this.renderSingleWorkPagesReadRows({ maxRows: Infinity }),
        loading: this.props.furthestPagesFetching,
      });
    } else {
      boxes.push({
        statCount: _.reduce(
          this.props.topDocs,
          function (memo, topDoc) {
            return memo + topDoc.pages_read;
          },
          0,
        ),
        statLabel: 'Pages Read',
        statCountExtra: this.pagesReadDisclaimer(),
        columnLabels: ['Most-Read Papers', dateRange + 'Pages'],
        panelRows: this.renderMultiWorkPagesReadRows({
          maxRows: this.maxPanelTableRows,
        }),
        modalRows: this.renderMultiWorkPagesReadRows({ maxRows: Infinity }),
        loading: this.props.topDocsFetching,
      });
    }

    boxes.push({
      statCount: this.props.topTrafficSources.length,
      statNoun: 'Traffic Source',
      columnLabels: ['Source', dateRange + 'Views'],
      panelRows: this.renderTrafficSourceRows({
        maxRows: this.maxPanelTableRows,
      }),
      modalRows: this.renderTrafficSourceRows({ maxRows: Infinity }),
      loading: this.props.topTrafficSourcesFetching,
    });

    boxes.push({
      statCount: this.props.topSearches.length,
      statNoun: 'Search',
      columnLabels: ['Search', dateRange + 'Views'],
      panelRows: this.renderSearchRows({ maxRows: this.maxPanelTableRows }),
      modalRows: this.renderSearchRows({ maxRows: Infinity }),
      loading: this.props.topSearchesFetching,
    });

    return boxes;
  };

  generateRows = (boxes) => {
    if (boxes.length <= 3) {
      return [boxes];
    } else {
      return [_.first(boxes, 3)].concat(this.generateRows(boxes.slice(3)));
    }
  };

  render() {
    const rows = this.generateRows(this.getAllBoxes());
    const isMobile = window.innerWidth <= 768; // Match our media query breakpoint

    return (
      <div className="container-fluid">
        <div
          className="row"
          style={{
            '@media (max-width: 768px)': {
              display: 'flex',
              flexDirection: 'column',
            },
          }}
        >
          <ImpactTopStat
            statCount={this.props.visitorCount}
            statNoun="Unique Visitor"
            loading={this.props.visitorCountFetching}
            mobile={isMobile}
          />
          <ImpactTopStat
            statCount={this.props.downloadCount}
            statNoun="Download"
            loading={this.props.downloadCountFetching}
            mobile={isMobile}
          />
          <ImpactTopStat
            statCount={this.props.hitCount}
            statNoun="View"
            loading={this.props.hitCountFetching}
            mobile={isMobile}
          />
        </div>
        {_.map(rows, function (boxes, key) {
          return (
            <div
              className="row"
              key={key}
              style={{
                '@media (max-width: 768px)': {
                  display: 'flex',
                  flexDirection: 'column',
                },
              }}
            >
              {_.map(boxes, function (boxProps, key) {
                return <ImpactBox {...boxProps} mobile={isMobile} key={key} />;
              })}
            </div>
          );
        })}
      </div>
    );
  }

  percentileDisclaimer = () => {
    let reason;
    if (this.havePercentileData()) {
      reason =
        'You have too few page views to have a percent ranking in this time range.';
    } else {
      reason = 'Percent ranking data is unavailable for this time range.';
    }

    return (
      <span
        className="js-tooltip u-fs52 u-lineHeight1 u-fw100 u-tcGrayLight"
        title={reason}
      >
        (N/A)
      </span>
    );
  };

  havePercentileData = () => {
    return (
      (isCurrentThirtyDays(this.props.displayedDates) ||
        this.props.twelveMonth) &&
      this.props.userPercentile.model
    );
  };

  bucketFurthestPages = (furthestPages, options) => {
    const buckets = [];
    const perBucket = furthestPages.length / (options.maxRows * 1.0);
    let processed = 0;
    let bucket = null;

    _.chain(furthestPages)
      .sortBy(_.identity)
      .each(function (page) {
        const full = processed >= buckets.length * perBucket;
        if (!bucket || (full && page > bucket.lte)) {
          bucket = {
            gte: page,
            count: 0,
          };
          buckets.push(bucket);
        }

        bucket.lte = page;
        bucket.count = bucket.count + 1;
        processed++;
      });

    return buckets;
  };

  pagesReadDisclaimer = () => {
    if (!this.props.showPagesReadDisclaimer) return;

    return (
      <sup className="u-fs52 u-lineHeight1">
        <span
          className="u-fs18 js-tooltip u-tcGrayDark"
          title="Page-read data is unavailable for some views in this time range."
          style={{ cursor: 'default' }}
        >
          *
        </span>
      </sup>
    );
  };
}
